import React from 'react';

import { MergedIntervalWithDates, MonthTuple, weekdayNames, When } from '@src/common/calendar';
import { FeaturedTariffPreviewMode, Scheduling } from '@src/core/buyerApiClient';
import { Container, DateArea, DateAreaText, WeekDayNameArea } from './styles';
import useTranslate from '@src/i18n/useTranslate';
import { infoCents } from '@src/common';
import { getSchedulingsTariffPreview } from '@src/common/tariffs';

interface CalendarMonthViewProps {
  filteredDates: Array<MergedIntervalWithDates>;
  pending: boolean;
  monthTuple: MonthTuple;
  onSelectDate: (date: string) => void;
  tariffToShowThePrice?: FeaturedTariffPreviewMode | null;
}

const CalendarMonthView = (props: CalendarMonthViewProps) => {
  const { filteredDates, monthTuple, onSelectDate, pending, tariffToShowThePrice } = props;

  const { i18n } = useTranslate();

  const getSchedulingPrice = (schedulings?: Scheduling[]) => {
    if (!schedulings || schedulings.length === 0 || !tariffToShowThePrice) {
      return null;
    }

    const priceCents = getSchedulingsTariffPreview(schedulings, tariffToShowThePrice);
    if (typeof priceCents !== 'number') {
      return null;
    }

    return infoCents(priceCents);
  };

  return (
    <Container aria-label='month-days'>
      {weekdayNames.map((weekday) => (
        <WeekDayNameArea key={weekday} pending={pending}>
          {i18n(weekday).substring(0, 3)}
        </WeekDayNameArea>
      ))}

      {filteredDates.map(({ date, availableSchedulings, unavailableSchedulings }) => {
        const isAvailable = Boolean(availableSchedulings?.length);
        const isSoldOut = Boolean(unavailableSchedulings?.length) && !isAvailable;

        const schedulingPrice =
          tariffToShowThePrice && isAvailable ? getSchedulingPrice(availableSchedulings as Scheduling[]) : null;

        return (
          <DateArea
            onClick={() => (availableSchedulings?.length ? onSelectDate(date.dateString) : null)}
            isAvailable={isAvailable}
            key={date.dateString}
            when={date.when}
            pending={pending}
            isSoldOut={isSoldOut}
            date={new Date(date.year, date.month - 1, date.date)}
            selectedMonth={monthTuple[1]}
            aria-label='day-button'
            disabled={!isAvailable}
            aria-disabled={!isAvailable}
            data-cy={isAvailable ? 'available_scheduling' : 'disabled_scheduling'}
          >
            <DateAreaText data-testid='day' isAvailable={!!isAvailable} isFuture={date.when == When.FUTURE}>
              {date.date}
            </DateAreaText>

            {schedulingPrice ? (
              <DateAreaText isPrice isAvailable={!!isAvailable} isFuture={date.when == When.FUTURE}>
                {schedulingPrice.currency}
                {schedulingPrice.priceInt}
              </DateAreaText>
            ) : null}
          </DateArea>
        );
      })}
    </Container>
  );
};

export default CalendarMonthView;
